import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import GameContentProvider from '../providers/GameContentProvider';
import { useLocation } from 'react-router-dom';
import "./GameOverview.css";
import { useTranslation } from 'react-i18next';
import SurveyComponent from '../SurveyComponent';
import Toggle from '../Toggle';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';

// Component to dynamically update map center and zoom
const UpdateMapCenter = ({ center, zoom }) => {
    const map = useMap();
    map.setView(center, zoom);
    return null;
};

const GameOverview = () => {
    const { t, i18n } = useTranslation();
    const [gameId, setGameId] = useState(null);
    const [playId, setPlayId] = useState(null);
    const [history, setHistory] = useState(null);
    const [recommendations, setRecommendations] = useState(null);
    const [mapCenter, setMapCenter] = useState([42.6977, 23.3219]);
    const [mapZoom, setMapZoom] = useState(14);
    const [showSurvey, setShowSurvey] = useState(false);
    const [surveySubmitted, setSurveySubmited] = useState(false);
    const [showRecommendations, setShowRecommendations] = useState(false);
    const [isAdminState, setIsAdminState] = useState(false);
    const location = useLocation();
    const gameContentProvider = new GameContentProvider();
    const markersRef = useRef(null);

    const greenIcon = new Icon({
        iconUrl: '/assets/marker-green.png',
        iconSize: [35, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
    });

    const redIcon = new Icon({
        iconUrl: '/assets/marker.png',
        iconSize: [35, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
    });

    useEffect(() => {
        const { game, play_id, isAdmin } = location.state || {};
        if (game) setGameId(game);
        if (play_id) setPlayId(play_id);
        if (isAdmin) {
            setIsAdminState(true);
            setSurveySubmited(true); // Don't show survey to admins
        }
    }, [location.state]);

    useEffect(() => {
        const fetchData = async () => {
            if (!gameId || isAdminState) return;
            try {
                let token = localStorage.getItem('token') || localStorage.getItem('guest_token');
                const result = await gameContentProvider.checkSurveySubmited(token, gameId);
                if (result?.success) setSurveySubmited(true);
            } catch (error) {
                console.error('Error fetching survey status:', error);
            }
        };
        fetchData();
    }, [gameId, isAdminState]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result;
                let userLang = (i18n.language || 'en').substring(0, 2);
                if (isAdminState) {
                    let adminToken = localStorage.getItem('admin_token');
                    result = await gameContentProvider.getAdminPlayHistory(playId, adminToken, userLang);
                } else {
                    let token = localStorage.getItem('token') || localStorage.getItem('guest_token');
                    result = await gameContentProvider.getPlayHistory(playId, token, userLang);
                }
                if (result?.history?.steps?.length) {
                    let historyData = result.history;

                    const sanitizeDate = (dateStr) => {
                        if (!dateStr) return moment();
                        return moment(dateStr.replace('T', ' ').replace('Z', ''));
                    };

                    let prevTime = sanitizeDate(historyData.start_date);
                    let lastCompletedTime = prevTime;

                    historyData.steps.forEach((step) => {
                        if (step.completion_date && step.completion_date.trim() !== "Not Completed") {
                            const completedTime = sanitizeDate(step.completion_date);
                            const dur = moment.duration(Math.max(0, completedTime.diff(prevTime)));

                            let h = Math.floor(dur.asHours());
                            let m = dur.minutes();
                            let s = dur.seconds();

                            step.timeTaken = `${h > 0 ? h + 'h ' : ''}${m}m ${s}s`;

                            prevTime = completedTime;
                            if (completedTime.isAfter(lastCompletedTime)) {
                                lastCompletedTime = completedTime;
                            }
                        } else {
                            step.timeTaken = null;
                        }
                    });

                    const totalDur = moment.duration(Math.max(0, lastCompletedTime.diff(sanitizeDate(historyData.start_date))));
                    let th = Math.floor(totalDur.asHours());
                    let tm = totalDur.minutes();
                    let ts = totalDur.seconds();
                    historyData.actualGameDuration = `${th > 0 ? th + 'h ' : ''}${tm}m ${ts}s`;

                    setHistory(historyData);
                    setRecommendations(historyData.recommendations);
                    const firstStep = historyData.steps[0];
                    if (firstStep.latitude && firstStep.longitude) {
                        setMapCenter([firstStep.latitude, firstStep.longitude]);
                        setMapZoom(14);
                    }
                }
            } catch (error) {
                console.error('Error fetching history:', error);
            }
        };
        if (gameId && playId) fetchData();
    }, [gameId, playId, isAdminState]);

    const icons = {
        1: new Icon({ iconUrl: '/assets/marker_grab_and_go.png', iconSize: [35, 50] }),
        2: new Icon({ iconUrl: '/assets/marker_restaurants2.png', iconSize: [35, 50] }),
        3: new Icon({ iconUrl: '/assets/marker_games.png', iconSize: [35, 50] }),
        4: new Icon({ iconUrl: '/assets/marker_information.png', iconSize: [35, 50] }),
        5: new Icon({ iconUrl: '/assets/marker_bar.png', iconSize: [35, 50] }),
    };

    const renderMarkers = () => {
        if (!history?.steps) return null;
        return history.steps.map((step, index) => (
            step.latitude && step.longitude ? (
                <Marker key={index} position={[step.latitude, step.longitude]} icon={getMarkerIcon(step.completion_date)}>
                    <Popup>
                        <div>
                            <h4>{t('gameoverview.step')}: {step.step}</h4>
                            <p>{step.explanation}</p>
                            {step.timeTaken && (
                                <p><strong>{t('gameoverview.time_taken')}</strong> {step.timeTaken}</p>
                            )}
                        </div>
                    </Popup>
                </Marker>
            ) : null
        ));
    };

    const renderRecommendationMarkers = () => {
        if (!recommendations || !showRecommendations) return null;
        return recommendations.map((rec, index) => (
            <Marker key={`rec-${index}`} position={[rec.latitude, rec.longitude]} icon={icons[rec.type] || icons[1]}>
                <Popup>
                    <b>{rec.name}</b><br />{rec.description}
                </Popup>
            </Marker>
        ));
    };

    const getMarkerIcon = (completionDate) => {
        return completionDate !== "Not Completed" ? greenIcon : redIcon;
    };

    return (
        <div>
            {history && <h2 style={{ textAlign: 'center' }}>{t('good_job')} {history.team_name}!</h2>}
            {history && <h3 style={{ textAlign: 'center' }}>{t('gameoverview.game_duration')} {history.actualGameDuration}</h3>}
            <div className="MapContainerOverview">
                <MapContainer style={{ height: '400px', width: '90%', margin: '0 auto' }}>
                    <UpdateMapCenter center={mapCenter} zoom={mapZoom} />
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {renderMarkers()}
                    {renderRecommendationMarkers()}
                </MapContainer>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px' }}>
                    <span style={{ marginRight: '10px' }}>{t('show_recommendations')}</span>
                    <Toggle checked={showRecommendations} onChange={setShowRecommendations} />
                </div>
                {!surveySubmitted && (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                        <button onClick={() => setShowSurvey(true)} className="survey-btn-centered" style={{ cursor: 'pointer', padding: '10px 20px', fontSize: '1.2rem', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '5px' }}>
                            {t('Start 1 min Survey')}
                        </button>
                    </div>
                )}
                {showSurvey && (
                    <div className='overlay-survey'>
                        <SurveyComponent
                            gameId={gameId}
                            onSubmit={() => {
                                setShowSurvey(false);
                                setSurveySubmited(true); // Hide button after submission
                            }}
                        />
                    </div>
                )}
            </div>

            <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '20px', paddingBottom: '40px' }}>
                <h3 style={{ marginBottom: '20px', color: '#333' }}>
                    {t('follow_facebook_story', 'Follow us on Facebook and share your story!')}
                </h3>
                <a href="https://www.facebook.com/profile.php?id=61581409275021" target="_blank" rel="noopener noreferrer"
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                        backgroundColor: '#1877f2', color: 'white', padding: '12px 30px',
                        borderRadius: '30px', textDecoration: 'none', fontSize: '1.2rem',
                        fontWeight: '600', boxShadow: '0 8px 15px rgba(24, 119, 242, 0.3)',
                        transition: 'transform 0.2s', cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <FontAwesomeIcon icon={faFacebook} size="lg" /> Facebook
                </a>
            </div>
        </div>
    );
};

export default GameOverview;